import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { canPrintPurchaseOrder } from './usePurchaseOrderPermissions';
import { DocumentType } from '../../../../../enums/document-type';
import { usePrintSettings } from '../../../../../hooks/printers/use-print-settings';
import { downloadDocument, printDocument } from '../../../../../store/documents/documents.actions';
import { printPurchaseOrder } from '../../../../../store/printing/print.actions';

export const PurchaseOrderPrintDocumentType = Object.freeze({
  ordered: 0,
  received: 1,
});

/**
 * Hook for purchase order printing functionality
 * Uses centralized permission logic from purchase-order-permissions
 * @param {object} params - Parameters
 * @param {string[]} params.purchaseOrderIds - The IDs of the purchase order to print
 * @param {Array<import('../../api/purchase-order-api').PurchaseOrderRow>} [params.data] - Purchase orders data from the grid
 * @returns {object} An object containing:
 * - {boolean} isDisabled: Indicates if printing is disabled (true if there is not exactly one purchase order ID).
 * - {function} onClick: Function to trigger the purchase order print action.
 * - {function} onDownload: Function to trigger the download of the purchase order document.
 * - {function} onPrint: Function to trigger the print of the purchase order document with settings.
 */
export const usePrintPurchaseOrder = ({ purchaseOrderIds, data }) => {
  const dispatch = useDispatch();
  const printSettings = usePrintSettings(DocumentType.PurchaseOrder.printSettingKey);
  const [purchaseOrderId] = purchaseOrderIds;

  const isDisabled = useMemo(() => {
    const getSelectedPurchaseOrder = data?.find((po) => po.purchaseOrderId === +purchaseOrderId);

    const printAllowed = purchaseOrderIds.length === 1 && canPrintPurchaseOrder(getSelectedPurchaseOrder?.status || '');

    return !printAllowed;
  }, [purchaseOrderIds, purchaseOrderId, data]);

  const onClick = useCallback(
    (printType) => {
      if (purchaseOrderId) {
        dispatch(printPurchaseOrder(purchaseOrderId, printType));
      }
    },
    [dispatch, purchaseOrderId],
  );

  const onDownload = useCallback(() => {
    if (purchaseOrderId) {
      dispatch(downloadDocument(DocumentType.PurchaseOrder, purchaseOrderId));
    }
  }, [dispatch, purchaseOrderId]);

  const onPrint = useCallback(() => {
    if (purchaseOrderId) {
      dispatch(printDocument(DocumentType.PurchaseOrder, purchaseOrderId, printSettings));
    }
  }, [dispatch, purchaseOrderId, printSettings]);

  return useMemo(
    () => ({
      isDisabled,
      onClick,
      onDownload,
      onPrint,
    }),
    [isDisabled, onClick, onDownload, onPrint],
  );
};
